import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthenticationContext } from './AuthenticationProvider';
import axios from 'axios';
import { ProjectResponse } from './types/ProjectResponse';
import { useParams } from 'react-router-dom';
import { getTranslation } from '../utility';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

export interface IProject {
  projectId: number | undefined;
  projectName: string | undefined;
  projectDescription: string | undefined;
  projectImage: string | undefined;
}

export interface IProjectContext {
  projectData: IProject | undefined;
}

export interface IProjectProviderProps {
  children: any;
}

const ProjectContext = React.createContext<IProjectContext>({
  projectData: undefined,
});

const ProjectProvider = (props: IProjectProviderProps) => {
  const { children } = props;

  const { id } = useParams();
  const { isAuthenticated, token } = useContext(AuthenticationContext);
  const { i18n } = useTranslation();

  const [projectData, setProjectData] = useState<IProject | undefined>();
  const [fetch, setFetch] = useState(true);

  const authenticated = useCallback(() => {
    return token && isAuthenticated();
  }, [isAuthenticated, token]);

  const language = useCallback(() => {
    return i18n.language as 'en' | 'fr';
  }, [i18n]);

  const getProject = useCallback(() => {
    async function fetchProject() {
      try {
        const projectsResponse = await axios.get<ProjectResponse>(
          `/api/projects/${id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer: ${token}`,
            },
          }
        );

        return {
          projectData: projectsResponse.data,
        };
      } catch (error) {
        console.log(error);
        return { projectData: null, resourcesData: null };
      }
    }

    return fetchProject();
  }, [token, id]);

  useEffect(() => {
    if (authenticated() && fetch) {
      let obj: IProject = {
        projectId: id ? parseInt(id as string) : 0,
        projectName: '',
        projectDescription: '',
        projectImage: '/default-image.png',
      };
      trackPromise(getProject())
        .then(({ projectData }) => {
          if (projectData?.project) {
            obj.projectName = getTranslation(
              language(),
              projectData.project.name
            );
            obj.projectDescription = getTranslation(
              language(),
              projectData.project.description
            );
          }

          setProjectData(obj);
          setFetch(false);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [token, authenticated, id, language, getProject, fetch]);

  return (
    <ProjectContext.Provider
      value={{
        projectData,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export { ProjectContext, ProjectProvider };
